import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import DashboardNavigation from '../../components/ui/DashboardNavigation';
import EquipmentFilters from './components/EquipmentFilters';
import EquipmentTable from './components/EquipmentTable';
import EquipmentDetailsModal from './components/EquipmentDetailsModal';
import MaintenanceLogModal from './components/MaintenanceLogModal';

const EquipmentManagement = () => {
  const { userProfile } = useAuth();
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [viewEquipmentId, setViewEquipmentId] = useState(null);
  const [maintenanceModalOpen, setMaintenanceModalOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState({ column: 'name', direction: 'asc' });
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    status: 'all',
    location: 'all',
    condition: 'all'
  });

  // Mock equipment data
  const mockEquipment = [
  {
    id: 1,
    name: 'Treadmill Pro X500',
    category: 'Cardio',
    type: 'Treadmill',
    location: 'Main Floor - Zone A',
    status: 'operational',
    condition: 'excellent',
    lastMaintenance: '2026-01-15T00:00:00',
    nextMaintenance: '2026-02-15T00:00:00',
    purchaseDate: '2024-06-10T00:00:00',
    warrantyExpiry: '2027-06-10T00:00:00',
    serialNumber: 'TRD-X500-2024-001',
    manufacturer: 'FitnessTech',
    model: 'X500 Pro',
    cost: 4500.00,
    maintenanceCount: 3,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b61c3fda-1765201154064.png",
    imageAlt: 'Professional treadmill with digital display'
  },
  {
    id: 2,
    name: 'Smith Machine Deluxe',
    category: 'Strength',
    type: 'Smith Machine',
    location: 'Weight Room - Section B',
    status: 'operational',
    condition: 'good',
    lastMaintenance: '2026-01-10T00:00:00',
    nextMaintenance: '2026-03-10T00:00:00',
    purchaseDate: '2023-09-15T00:00:00',
    warrantyExpiry: '2026-09-15T00:00:00',
    serialNumber: 'SMD-DLX-2023-045',
    manufacturer: 'IronWorks',
    model: 'Deluxe Pro',
    cost: 3200.00,
    maintenanceCount: 8,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ce2ce8a2-1766490513164.png",
    imageAlt: 'Heavy duty smith machine with safety bars'
  },
  {
    id: 3,
    name: 'Elliptical Trainer E200',
    category: 'Cardio',
    type: 'Elliptical',
    location: 'Main Floor - Zone C',
    status: 'maintenance',
    condition: 'fair',
    lastMaintenance: '2026-01-20T00:00:00',
    nextMaintenance: '2026-01-27T00:00:00',
    purchaseDate: '2022-11-20T00:00:00',
    warrantyExpiry: '2025-11-20T00:00:00',
    serialNumber: 'ELP-E200-2022-078',
    manufacturer: 'CardioMax',
    model: 'E200 Elite',
    cost: 2800.00,
    maintenanceCount: 12,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1130f081e-1768678811617.png",
    imageAlt: 'Elliptical trainer with adjustable resistance'
  },
  {
    id: 4,
    name: 'Rowing Machine R400',
    category: 'Cardio',
    type: 'Rowing Machine',
    location: 'Main Floor - Zone B',
    status: 'out-of-service',
    condition: 'poor',
    lastMaintenance: '2025-12-05T00:00:00',
    nextMaintenance: '2026-01-28T00:00:00',
    purchaseDate: '2021-03-12T00:00:00',
    warrantyExpiry: '2024-03-12T00:00:00',
    serialNumber: 'ROW-R400-2021-023',
    manufacturer: 'RowPro',
    model: 'R400 Commercial',
    cost: 1900.00,
    maintenanceCount: 18,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_10319a031-1765201153442.png",
    imageAlt: 'Commercial rowing machine with digital monitor'
  },
  {
    id: 5,
    name: 'Leg Press Machine LP800',
    category: 'Strength',
    type: 'Leg Press',
    location: 'Weight Room - Section A',
    status: 'operational',
    condition: 'excellent',
    lastMaintenance: '2026-01-18T00:00:00',
    nextMaintenance: '2026-04-18T00:00:00',
    purchaseDate: '2024-08-22T00:00:00',
    warrantyExpiry: '2029-08-22T00:00:00',
    serialNumber: 'LGP-LP800-2024-012',
    manufacturer: 'PowerLift',
    model: 'LP800 Heavy Duty',
    cost: 5200.00,
    maintenanceCount: 1,
    image: "https://images.unsplash.com/photo-1712992031203-45538d32ab43",
    imageAlt: 'Heavy duty leg press machine with weight plates'
  },
  {
    id: 6,
    name: 'Cable Crossover Station',
    category: 'Strength',
    type: 'Cable Machine',
    location: 'Weight Room - Section C',
    status: 'operational',
    condition: 'good',
    lastMaintenance: '2026-01-12T00:00:00',
    nextMaintenance: '2026-02-12T00:00:00',
    purchaseDate: '2023-05-18T00:00:00',
    warrantyExpiry: '2026-05-18T00:00:00',
    serialNumber: 'CBL-CCS-2023-089',
    manufacturer: 'FlexFit',
    model: 'CCS Pro',
    cost: 4100.00,
    maintenanceCount: 6,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_13d372d3f-1769369908864.png",
    imageAlt: 'Cable crossover station with dual weight stacks'
  }];


  const [equipment, setEquipment] = useState(mockEquipment);

  // Check role-based access
  const hasAccess = ['owner', 'manager']?.includes(userProfile?.role);

  useEffect(() => {
    if (!hasAccess) {
      // Redirect or show access denied
      console.warn('Access denied: User does not have permission to view equipment management');
    }
  }, [hasAccess]);

  // Filter equipment
  const filteredEquipment = equipment?.filter((item) => {
    const matchesSearch = filters?.search === '' ||
    item?.name?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
    item?.serialNumber?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
    item?.manufacturer?.toLowerCase()?.includes(filters?.search?.toLowerCase());

    const matchesCategory = filters?.category === 'all' || item?.category === filters?.category;
    const matchesStatus = filters?.status === 'all' || item?.status === filters?.status;
    const matchesLocation = filters?.location === 'all' || item?.location?.includes(filters?.location);
    const matchesCondition = filters?.condition === 'all' || item?.condition === filters?.condition;

    return matchesSearch && matchesCategory && matchesStatus && matchesLocation && matchesCondition;
  });

  // Sort equipment
  const sortedEquipment = [...filteredEquipment]?.sort((a, b) => {
    const aValue = a?.[sortConfig?.column];
    const bValue = b?.[sortConfig?.column];

    if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (column) => {
    setSortConfig((prev) => ({
      column,
      direction: prev?.column === column && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      category: 'all',
      status: 'all',
      location: 'all',
      condition: 'all'
    });
  };

  const handleSelectEquipment = (id, checked) => {
    setSelectedEquipment((prev) =>
    checked ? [...prev, id] : prev?.filter((eqId) => eqId !== id)
    );
  };

  const handleSelectAll = (checked) => {
    setSelectedEquipment(checked ? sortedEquipment?.map((eq) => eq?.id) : []);
  };

  const handleViewEquipment = (id) => {
    setViewEquipmentId(id);
  };

  const handleCloseDetailsModal = () => {
    setViewEquipmentId(null);
  };

  const handleLogMaintenance = () => {
    setMaintenanceModalOpen(true);
  };

  const handleCloseMaintenanceModal = () => {
    setMaintenanceModalOpen(false);
  };

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background">
        <RoleBasedHeader />
        <div className="page-content-wrapper">
          <DashboardNavigation />
        </div>
        <div className="container mx-auto px-4 py-8">
          <div className="bg-card rounded-xl p-8 border border-border text-center">
            <Icon name="ShieldAlert" size={48} className="mx-auto mb-4 text-error" />
            <h2 className="text-2xl font-bold text-foreground mb-2">Access Denied</h2>
            <p className="text-muted-foreground">You do not have permission to access equipment management.</p>
            <p className="text-sm text-muted-foreground mt-2">This feature is only available to gym owners and managers.</p>
          </div>
        </div>
      </div>);

  }

  const viewEquipment = equipment?.find((eq) => eq?.id === viewEquipmentId);

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader />
      <div className="page-content-wrapper">
        <DashboardNavigation />
      </div>
      
      <div className="container mx-auto px-4 lg:px-8 py-6 lg:py-8">
        {/* Header */}
        <div className="mb-6 lg:mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground mb-2">Equipment Management</h1>
              <p className="text-muted-foreground">Track, maintain, and manage all gym equipment across facilities</p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                iconName="Download"
                iconPosition="left">

                Export Report
              </Button>
              <Button
                variant="primary"
                iconName="Plus"
                iconPosition="left">

                Add Equipment
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Equipment</p>
                <p className="text-2xl font-bold text-foreground">{equipment?.length}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="Package" size={24} className="text-primary" />
              </div>
            </div>
          </div>
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Operational</p>
                <p className="text-2xl font-bold text-success">{equipment?.filter((eq) => eq?.status === 'operational')?.length}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center">
                <Icon name="CheckCircle2" size={24} className="text-success" />
              </div>
            </div>
          </div>
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">In Maintenance</p>
                <p className="text-2xl font-bold text-warning">{equipment?.filter((eq) => eq?.status === 'maintenance')?.length}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-warning/10 flex items-center justify-center">
                <Icon name="Wrench" size={24} className="text-warning" />
              </div>
            </div>
          </div>
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Out of Service</p>
                <p className="text-2xl font-bold text-error">{equipment?.filter((eq) => eq?.status === 'out-of-service')?.length}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-error/10 flex items-center justify-center">
                <Icon name="AlertCircle" size={24} className="text-error" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6">
          <EquipmentFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters} />

        </div>

        {/* Bulk Actions */}
        {selectedEquipment?.length > 0 &&
        <div className="mb-4 bg-primary/10 rounded-lg p-4 border border-primary/20">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-foreground">
                {selectedEquipment?.length} equipment selected
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" iconName="Wrench" iconPosition="left" onClick={handleLogMaintenance}>
                  Log Maintenance
                </Button>
                <Button variant="outline" size="sm" iconName="AlertTriangle" iconPosition="left">
                  Report Issue
                </Button>
                <Button variant="outline" size="sm" iconName="Trash2" iconPosition="left">
                  Remove
                </Button>
              </div>
            </div>
          </div>
        }

        {/* Equipment Table */}
        <EquipmentTable
          equipment={sortedEquipment}
          selectedEquipment={selectedEquipment}
          onSelectEquipment={handleSelectEquipment}
          onSelectAll={handleSelectAll}
          onSort={handleSort}
          sortConfig={sortConfig}
          onViewEquipment={handleViewEquipment} />


        {/* Results Summary */}
        <div className="mt-4 text-center text-sm text-muted-foreground">
          Showing {sortedEquipment?.length} of {equipment?.length} equipment items
        </div>
      </div>

      {/* Equipment Details Modal */}
      {viewEquipmentId && viewEquipment &&
      <EquipmentDetailsModal
        equipment={viewEquipment}
        onClose={handleCloseDetailsModal} />

      }

      {/* Maintenance Log Modal */}
      {maintenanceModalOpen &&
      <MaintenanceLogModal
        selectedEquipment={selectedEquipment}
        onClose={handleCloseMaintenanceModal} />

      }
    </div>);

};

export default EquipmentManagement;